import { useEffect, useMemo, useState } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';

import { selectedPeriodVar } from 'app/apollo/reaction';
import {
    getAddedDishes,
    removeAddedDish,
    subscribeOnAddedDishesChange,
    TAddedDish,
} from 'app/views/Basket/components/BasketNewUpsell/components/BasketTopDishCard/addedPeriodStorage';
import { FURTHER_PERIOD_CART_SECTIONS } from 'app/views/Basket/BasketWithCustomization/graphql/basketWithCustomization.graphql';

/**
 * @description блюда, добавленные с баннера топ-блюда в чужие циклы.
 * Хранилище не реактивно, поэтому список перечитывается по событию
 * (то же, что локальный useAddedDishes в FurtherPeriodDishes)
 */
export const useAddedDishes = (): TAddedDish[] => {
    const [addedDishes, setAddedDishes] = useState<TAddedDish[]>(getAddedDishes);

    useEffect(
        () => subscribeOnAddedDishesChange(() => setAddedDishes(getAddedDishes())),
        [],
    );

    return addedDishes;
};

export type TFurtherPeriodOrder = {
    period: string | null;
    shouldShow: boolean;
};

/**
 * @description видит ли пользователь две корзины — добавлял ли блюдо
 * из блока топ-блюда не в текущий цикл. Запись в sessionStorage
 * создаётся только при таком добавлении (BasketTopDishCard), поэтому
 * её наличие и есть признак доп. заказа на следующий цикл.
 *
 * @returns period — цикл доп. заказа (нужен для текста уведомления),
 * shouldShow — показывать ли уведомление
 *
 * @note проверяем и запись, и корзину доп. цикла: одной записи мало —
 * корзину могли очистить в обход блока доп. цикла, и запись осталась
 * висеть. Одной корзины тоже мало: пользователь мог просто переключиться
 * на следующий цикл, выбрать там блюда из меню — тогда доп. инвойс
 * создавать не нужно. Если корзина доп. цикла пуста — выкидываем запись,
 * иначе чекаут создаст пустой доп. инвойс
 */
export const useFurtherPeriodOrder = (): TFurtherPeriodOrder => {
    const addedDishes = useAddedDishes();
    const selectedPeriod = useReactiveVar(selectedPeriodVar);

    /* @note с баннера всегда одно блюдо, поэтому периодов доп. заказа
     * бывает не больше одного — то же допущение у getNextPeriodToInvoice */
    const furtherPeriod = useMemo(
        () => addedDishes
            .find(({ period }) => Boolean(period) && period !== selectedPeriod)?.period || null,
        [addedDishes, selectedPeriod],
    );

    /* @note cache-and-network: пустую корзину подтверждаем ответом сети —
     * пока он не пришёл (loading) или ушёл в ошибку, решаем по записи,
     * чтобы не спрятать уведомление о реальном доп. заказе */
    const { data, loading, error } = useQuery(FURTHER_PERIOD_CART_SECTIONS, {
        variables: { period: furtherPeriod || '' },
        skip: !furtherPeriod,
        fetchPolicy: 'cache-and-network',
    });

    const isFurtherCartEmpty = useMemo(() => {
        if (!furtherPeriod || loading || error || !data) return false;

        const sections = data?.cart?.sections || [];

        return !sections.some((section: any) => (section?.items || [])
            .some((item: any) => Boolean(item?.dish_id) && item?.portions > 0));
    }, [data, error, furtherPeriod, loading]);

    useEffect(() => {
        if (!furtherPeriod || !isFurtherCartEmpty) return;

        addedDishes
            .filter(({ period }) => period === furtherPeriod)
            .forEach(({ dishId }) => removeAddedDish(dishId));
    }, [addedDishes, furtherPeriod, isFurtherCartEmpty]);

    return {
        period: furtherPeriod,
        shouldShow: Boolean(furtherPeriod) && !isFurtherCartEmpty,
    };
};
